import { ChangeDetectorRef, Component, Input, inject } from '@angular/core';
import { SpendingTimelineDataItem } from './spending-timeline.types';
import { SpendingTimelineService } from './spending-timeline.service';
import { BudgetTimeframeDataItem } from '../widget.service';
import { SelectedValue } from 'app/shared/types/select-groups.interface';
import { BudgetAmountsSummaryData } from '../budget-amounts-summary/budget-amounts-summary.component';
import { BudgetAmountsSummaryService } from 'app/shared/services/budget-amounts-summary.service';
import { prepareBudgetObjectTotals } from 'app/shared/utils/budget.utils';
import { BudgetObjectTotals } from 'app/shared/types/budget-object-totals.interface';
import { BudgetCegTimelineService } from '@manage-ceg/services/budget-ceg-timeline.service';
import { UserPreferencesService } from '../../manage-ceg-table/services/user-preferences.service';
import { Budget } from '../../shared/types/budget.interface';
import { managePageUserPreferenceStrings } from '../../manage-ceg-table/constants/user-preferences.constants';
import { TableColumnId } from '../../manage-ceg-table/components/manage-ceg-table-container/manage-ceg-table-container.enum';
import { BudgetService } from '../../shared/services/backend/budget.service';
import { UtilityService } from '../../shared/services/utility.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'spending-timeline',
  templateUrl: './spending-timeline.component.html',
  styleUrls: ['./spending-timeline.component.scss'],
  providers: [SpendingTimelineService]
})
export class SpendingTimelineComponent {
  timelineItems: SpendingTimelineDataItem[];
  selectedStatuses: string[];
  summaryTooltipData: BudgetAmountsSummaryData[];
  dataItemsList: BudgetTimeframeDataItem[]
  private readonly budgetCegTimelineService = inject(BudgetCegTimelineService);
  legendsList = ['Available Budget'];
  loadData = false;

  @Input() cegFlagEnabled = false;
  @Input() isManageTableTimeline = false;
  @Input() companyCurrency: string;
  @Input() selectedTimeframeIds: SelectedValue[] = [];
  @Input() set selectedStatusNames(selectedStatuses: string[]) {
    this.selectedStatuses = selectedStatuses;
    if (!this.timelineItems || !this.timelineItems.length) {
      return;
    }
    this.updateDataForDisplay();
  };

 @Input() set data(dataItems: BudgetTimeframeDataItem[]) {
    this.dataItemsList = dataItems;

    if (this.cegFlagEnabled) {
      if (this.budget) {
        this.updateDataBasedOnCAB();
      }
      return;
    }

    // Default processing (non-CEG)
    this.loadData = false;
    this.summaryTooltipData = dataItems.map(item => {
      const budgetObjectTotals: BudgetObjectTotals = {
        remaining_allocated: item.budget_remaining_amount,
        available: item.budget_remaining_amount,
        closed: item.closed,
        committed: item.committed,
        overdue: item.overdue,
        planned: item.planned,
        total: item.total,
        under_budget: item.under_budget,
        actual: item?.spent,
        forecast: item?.forecast
      };
      let currencyCode: string;
      this.budgetCegTimelineService.companyCurrency$.subscribe(value => currencyCode = value['code']);
      this.companyCurrency = currencyCode;

      return this.amountsSummaryService.createTooltipData({
        item: prepareBudgetObjectTotals(budgetObjectTotals, item.locked),
        title: item.company_budget_alloc_name,
        currency: this.companyCurrency,
      }, true, false);
    });

    this.timelineItems = this.spendingTimelineService.convertRawDataToToSpendingTimelineData(dataItems);
    this.updateDataForDisplay();
    this.cdr.detectChanges();
  }


  @Input() set budget(b: Budget) {
    this._budget = b;

    if (this.cegFlagEnabled && this.dataItemsList?.length) {
      this.updateDataBasedOnCAB();
    }
  }
  get budget(): Budget {
    return this._budget;
  }
  private _budget: Budget;


  constructor(
    private readonly amountsSummaryService: BudgetAmountsSummaryService,
    private readonly spendingTimelineService: SpendingTimelineService,
    private userPreferencesService: UserPreferencesService,
    private budgetService: BudgetService,
    private cdr: ChangeDetectorRef,
    private utilityService: UtilityService
  ) {
  }


  updateDataForDisplay() {
    this.loadData = true;
    this.utilityService.showLoading(false);
    if (!this.selectedStatuses || !this.selectedStatuses.length) {
      return;
    }
    this.timelineItems.forEach(item => {
      item.spending = item.spending.filter(sp => this.selectedStatuses.includes(sp.id));
      item.totalSpent = item.spending.reduce((total, sp) => total + sp.val, 0);
      item.overSpend = item.totalSpent > item.budget ? item.totalSpent - item.budget : 0;
    })

  }

  updateDataBasedOnCAB() {
    this.utilityService.showLoading(true);
    let tableConfig = [];

    if(this.isManageTableTimeline){
      const budgetTableAreaDetails = managePageUserPreferenceStrings['budget_table'].personalization_keys;
      const personalizationKeys = [
        budgetTableAreaDetails.table_config
      ];
      this.userPreferencesService.getUserPersonalizationDataConfig(
        this.budget.company.toString(),
        this.budget.id.toString(),
        managePageUserPreferenceStrings['budget_table'].name,
        personalizationKeys
      ).subscribe((data) => {

        if (!data || !data.ceg_table_config) {
          // Just replaced v2 to V3 ideally we shouldn't be making an API call for this (but this was existing as Part of CAB chart impl)
          this.budgetService.getBudgetsV3CABFormula(this.budget.company, this.budget.id).subscribe((cabData) => {
            tableConfig = [...cabData];
            this.setCabData(tableConfig);
          });
        }
        else {
          tableConfig = data?.ceg_table_config?.columns_to_show;
          this.setCabData(tableConfig);
        }
      });
    }
    else{
      this.budgetService.getBudgetsV3CABFormula(this.budget.company, this.budget.id).subscribe((cabData) => {
          tableConfig = [...cabData];
          this.setCabData(tableConfig);
        });
    }
    
  }

  async setCabData(tableConfig){
    if (this.cegFlagEnabled && this.dataItemsList?.length) {
        const columnToLegendMap = {
          [TableColumnId.Planned]: 'Remaining Planned Budget',
          [TableColumnId.Committed]: 'Remaining Committed Budget',
          [TableColumnId.Actual]: 'Actual Spend',
          [TableColumnId.Forecast]: 'Forecast',
        };
    
        const newLegendsList = ['Available Budget'];
        
        Object.entries(columnToLegendMap).forEach(([key, label]) => {
          if (tableConfig.includes(Number(key))) {
            newLegendsList.unshift(label);
          }
        });
        
        if (this.dataItemsList.some(item => item.overdue > 0)) {
          newLegendsList.push('Over Budget');
        }

        this.legendsList = newLegendsList;

        const currencyValue = await firstValueFrom(this.budgetCegTimelineService.companyCurrency$);
        this.companyCurrency = currencyValue['code'];

        this.summaryTooltipData = this.dataItemsList.map(item => {
          const budgetObjectTotals: BudgetObjectTotals = {
            remaining_allocated: item.budget_remaining_amount,
            available: item.budget_remaining_amount,
            closed: item.closed,
            committed: tableConfig.includes(TableColumnId.Committed) ? item.committed : 0,
            overdue: item.overdue,
            planned: tableConfig.includes(TableColumnId.Planned) ? item.planned : 0,
            total: item.total,
            under_budget: item.under_budget,
            actual: tableConfig.includes(TableColumnId.Actual) ? item.spent : 0,
            forecast: tableConfig.includes(TableColumnId.Forecast) ? item.forecast : 0
          };
          return this.amountsSummaryService.createTooltipData({
            item: prepareBudgetObjectTotals(budgetObjectTotals, item.locked),
            title: item.company_budget_alloc_name,
            currency: this.companyCurrency,
          }, true, true);
        });

        this.timelineItems = this.spendingTimelineService.convertRawDataToToSpendingTimelineData(this.dataItemsList,this.isManageTableTimeline , tableConfig, this.cegFlagEnabled);
        if(this.isManageTableTimeline && !tableConfig.includes(TableColumnId.Budget)) {
         this.legendsList = this.legendsList.filter(legend => legend !== columnToLegendMap[TableColumnId.Forecast]); 
         this.summaryTooltipData = this.summaryTooltipData.map(tooltip => ({ ...tooltip, isSmartCellOnForecast: true }))
        }
        this.updateDataForDisplay();
        this.cdr.detectChanges();
      }
  }

  ngOnChanges() {
    if (this.cegFlagEnabled && this._budget && this.dataItemsList?.length) {
      this.updateDataBasedOnCAB();
    }
  }
}
